'use client';

import { subscribeSpartanProductScroll } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/store';

const PARALLAX_ROOT_SELECTOR = '[data-spartan-parallax-root]';
const PARALLAX_TARGET_SELECTOR = '[data-spartan-parallax]';
const NEAR_VIEWPORT_ROOT_MARGIN = '50% 0px';
const DESKTOP_MEDIA_QUERY = '(min-width: 64rem)';
const NATIVE_PARALLAX_SUPPORT_QUERY =
  '(animation-timeline: view()) and (animation-range: entry)';
const FIREFOX_SUPPORT_QUERY = '(-moz-appearance: none)';
const FALLBACK_FRAME_INTERVAL_MS = 15;

interface ParallaxTarget {
  element: HTMLElement;
  startVh: number;
  endVh: number;
  lastTransform?: string;
}

interface ParallaxScene {
  height: number;
  mode: 'fold' | 'through';
  root: HTMLElement;
  top: number;
  targets: ParallaxTarget[];
}

const clampProgress = (value: number): number =>
  Math.min(1, Math.max(0, value));

export const getSpartanParallaxFallbackProgress = (
  mode: ParallaxScene['mode'],
  rect: Pick<DOMRect, 'height' | 'top'>,
  viewportHeight: number
): number => {
  if (rect.height <= 0 || viewportHeight <= 0) return 0;

  if (mode === 'fold') {
    return clampProgress(-rect.top / rect.height);
  }

  return clampProgress(
    (viewportHeight - rect.top) / (viewportHeight + rect.height)
  );
};

export const shouldUseSpartanParallaxFallback = (): boolean =>
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  CSS.supports(FIREFOX_SUPPORT_QUERY) &&
  !CSS.supports(NATIVE_PARALLAX_SUPPORT_QUERY);

const readVhValue = (element: HTMLElement, property: string): number => {
  const value = element.style.getPropertyValue(property);
  const parsed = Number.parseFloat(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

const getTargets = (
  root: HTMLElement,
  isDesktop: boolean
): ParallaxTarget[] => {
  const breakpoint = isDesktop ? 'desktop' : 'mobile';

  return Array.from(
    root.querySelectorAll<HTMLElement>(PARALLAX_TARGET_SELECTOR)
  ).map((element) => ({
    element,
    startVh: readVhValue(element, `--spartan-parallax-${breakpoint}-start-y`),
    endVh: readVhValue(element, `--spartan-parallax-${breakpoint}-end-y`),
  }));
};

const getScenes = (): ParallaxScene[] => {
  const isDesktop = window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  const scrollY = window.scrollY;

  return Array.from(
    document.querySelectorAll<HTMLElement>(PARALLAX_ROOT_SELECTOR)
  ).flatMap((root) => {
    const mode = root.dataset.spartanParallaxRoot;
    if (mode !== 'fold' && mode !== 'through') return [];

    const targets = getTargets(root, isDesktop);
    if (!targets.length) return [];

    const rect = root.getBoundingClientRect();
    return [
      {
        height: rect.height,
        mode,
        root,
        top: rect.top + scrollY,
        targets,
      },
    ];
  });
};

const hasParallaxNode = (records: MutationRecord[]): boolean =>
  records.some((record) =>
    Array.from(record.addedNodes).some(
      (node) =>
        node instanceof Element &&
        (node.matches(PARALLAX_ROOT_SELECTOR) ||
          node.matches(PARALLAX_TARGET_SELECTOR) ||
          !!node.querySelector(
            `${PARALLAX_ROOT_SELECTOR}, ${PARALLAX_TARGET_SELECTOR}`
          ))
    )
  );

export const observeSpartanProductParallaxFallback = (): (() => void) => {
  if (!shouldUseSpartanParallaxFallback()) return () => undefined;

  let scenes: ParallaxScene[] = [];
  let activeRoots = new Set<HTMLElement>();
  let refreshFrameId: number | null = null;
  let lastScrollUpdateAt = Number.NEGATIVE_INFINITY;

  const update = () => {
    const viewportHeight = window.innerHeight;
    const scrollY = window.scrollY;

    scenes.forEach(({ height, mode, root, top, targets }) => {
      if (intersectionObserver && !activeRoots.has(root)) return;

      const progress = getSpartanParallaxFallbackProgress(
        mode,
        { height, top: top - scrollY },
        viewportHeight
      );

      targets.forEach((target) => {
        const startVh = mode === 'fold' ? 0 : target.startVh;
        const translateVh = startVh + (target.endVh - startVh) * progress;
        const translatePx = (translateVh / 100) * viewportHeight;
        const transform = `translate3d(0, ${translatePx.toFixed(3)}px, 0)`;

        if (target.lastTransform === transform) return;
        target.lastTransform = transform;
        target.element.style.transform = transform;
      });
    });
  };

  const intersectionObserver =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver(
          (entries) => {
            entries.forEach(({ isIntersecting, target }) => {
              const root = target as HTMLElement;

              if (isIntersecting) {
                activeRoots.add(root);
              } else {
                activeRoots.delete(root);
              }
            });
            update();
          },
          { rootMargin: NEAR_VIEWPORT_ROOT_MARGIN }
        );

  const refresh = () => {
    refreshFrameId = null;
    const previousElements = new Set(
      scenes.flatMap(({ targets }) => targets.map(({ element }) => element))
    );
    const nextScenes = getScenes();
    const nextElements = new Set(
      nextScenes.flatMap(({ targets }) => targets.map(({ element }) => element))
    );

    previousElements.forEach((element) => {
      if (!nextElements.has(element)) element.style.removeProperty('transform');
    });

    scenes = nextScenes;
    activeRoots = new Set(scenes.map(({ root }) => root));
    intersectionObserver?.disconnect();
    scenes.forEach(({ root }) => intersectionObserver?.observe(root));
    update();
  };

  const scheduleRefresh = () => {
    if (refreshFrameId !== null) return;
    refreshFrameId = window.requestAnimationFrame(refresh);
  };

  const handleScroll = () => {
    const now = window.performance.now();
    if (now - lastScrollUpdateAt < FALLBACK_FRAME_INTERVAL_MS) return;

    lastScrollUpdateAt = now;
    update();
  };

  const mutationObserver = new MutationObserver((records) => {
    if (hasParallaxNode(records)) scheduleRefresh();
  });

  const unsubscribeScroll = subscribeSpartanProductScroll(handleScroll);
  mutationObserver.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', scheduleRefresh, { passive: true });
  refresh();

  return () => {
    unsubscribeScroll();
    mutationObserver.disconnect();
    intersectionObserver?.disconnect();
    window.removeEventListener('resize', scheduleRefresh);
    if (refreshFrameId !== null) window.cancelAnimationFrame(refreshFrameId);
    scenes.forEach(({ targets }) => {
      targets.forEach(({ element }) =>
        element.style.removeProperty('transform')
      );
    });
  };
};
